import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/formatters';
import socket from '../services/socket';
import { API_BASE_URL } from '../config';
import FlickerText from './FlickerText';

const CompactHeader = ({ userId, isLoggedIn }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [accountData, setAccountData] = useState({
    balance: 0,
    totalValue: 0,
    performancePercent: 0,
    positions: [],
    initialBalance: 10000
  });
  const [stocks, setStocks] = useState({});
  const [marketState, setMarketState] = useState({
    isOpen: false,
    openTime: null,
    closeTime: null
  });

  // Fetch initial account data
  useEffect(() => {
    if (!userId || !isLoggedIn) {
      return;
    }

    const fetchAccountData = async () => {
      try {
        // Fetch balance from validate endpoint
        const validateResponse = await fetch(`${API_BASE_URL}/api/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: userId,
            token: localStorage.getItem('token')
          })
        });

        if (!validateResponse.ok) {
          throw new Error('Failed to fetch account data');
        }

        const userData = await validateResponse.json();
        const balance = userData.balance || 0;

        // Fetch positions separately
        let positions = [];
        try {
          const positionsResponse = await fetch(`${API_BASE_URL}/api/positions/${userId}`);
          if (positionsResponse.ok) {
            positions = await positionsResponse.json();
          }
        } catch (e) {
          console.error('Failed to fetch positions:', e);
        }

        // Fetch current stock prices to calculate position values
        let stocks = {};
        try {
          const stocksResponse = await fetch(`${API_BASE_URL}/api/stocks`);
          if (stocksResponse.ok) {
            const stocksData = await stocksResponse.json();
            stocksData.forEach(stock => {
              stocks[stock.id] = {
                currentPrice: parseFloat(stock.currentPrice) || parseFloat(stock.basePrice),
                ticker: stock.ticker
              };
            });
          }
        } catch (e) {
          console.error('Failed to fetch stocks:', e);
        }

        // Calculate position values with current prices
        const updatedPositions = positions.map(pos => {
          const stock = stocks[pos.stockId];
          if (stock) {
            const currentPrice = stock.currentPrice;
            const currentValue = pos.type === 'LONG'
              ? currentPrice * pos.shares
              : (2 * pos.entryPrice - currentPrice) * pos.shares;
            const profit = pos.type === 'LONG'
              ? (currentPrice - pos.entryPrice) * pos.shares
              : (pos.entryPrice - currentPrice) * pos.shares;

            return {
              ...pos,
              currentPrice,
              currentValue: parseFloat(currentValue.toFixed(2)),
              profit: parseFloat(profit.toFixed(2))
            };
          }
          return pos;
        }).filter(pos => {
          const isOpen = pos.status === 'open' || pos.status === 'OPEN' || !pos.status;
          return isOpen;
        });

        // Calculate total value from balance and positions
        const positionsValue = updatedPositions.reduce((sum, pos) => {
          return sum + (pos.currentValue || 0);
        }, 0);

        const totalValue = parseFloat((balance + positionsValue).toFixed(2));

        // Get initialBalance from leaderboard
        let initialBalance = balance;
        try {
          const leaderboardResponse = await fetch(`${API_BASE_URL}/api/leaderboard`);
          if (leaderboardResponse.ok) {
            const leaderboard = await leaderboardResponse.json();
            const userEntry = leaderboard.find(entry => entry.id === userId);
            if (userEntry && userEntry.initialBalance) {
              initialBalance = userEntry.initialBalance;
            }
          }
        } catch (e) {
          console.error('Failed to fetch leaderboard:', e);
        }

        // Calculate performance percent
        const performancePercent = initialBalance > 0
          ? parseFloat(((totalValue - initialBalance) / initialBalance * 100).toFixed(2))
          : 0;

        setAccountData({
          balance: balance,
          totalValue: totalValue,
          performancePercent: performancePercent,
          positions: updatedPositions,
          initialBalance: initialBalance
        });
      } catch (error) {
        console.error('Error fetching account data for header:', error);
      }
    };

    fetchAccountData();

    // Set up polling for updates - every 5 seconds to match stock update frequency
    const interval = setInterval(fetchAccountData, 5000);

    return () => clearInterval(interval);
  }, [userId, isLoggedIn]);

  // Listen to socket updates for real-time changes
  useEffect(() => {
    if (!userId || !isLoggedIn) return;

    const handleBalanceUpdate = (data) => {
      if (data.userId === userId) {
        setAccountData(prev => ({
          ...prev,
          balance: data.balance || prev.balance,
          totalValue: data.totalValue || prev.totalValue
        }));
      }
    };

    const handlePositionUpdate = (data) => {
      if (data.userId === userId) {
        setAccountData(prev => ({
          ...prev,
          positions: data.positions || prev.positions,
          totalValue: data.totalValue || prev.totalValue,
          performancePercent: data.performancePercent || prev.performancePercent
        }));
      }
    };

    const handleMarketStatus = (status) => {
      setMarketState({
        isOpen: status.isOpen,
        openTime: status.openTime,
        closeTime: status.closeTime
      });
    };

    const handleMarketBell = (data) => {
      if (data.type === 'OPENING_BELL') {
        setMarketState(prev => ({ ...prev, isOpen: true }));
      } else if (data.type === 'CLOSING_BELL') {
        setMarketState(prev => ({ ...prev, isOpen: false }));
      }
    };

    // Listen for stock price updates to recalculate position values
    const handleStockUpdates = (stocksData) => {
      const stocksMap = {};
      stocksData.forEach(stock => {
        stocksMap[stock.id] = {
          currentPrice: parseFloat(stock.currentPrice),
          ticker: stock.ticker
        };
      });
      setStocks(stocksMap);
    };

    const handleMarketData = (stocksData) => {
      const stocksMap = {};
      stocksData.forEach(stock => {
        stocksMap[stock.id] = {
          currentPrice: parseFloat(stock.currentPrice),
          ticker: stock.ticker
        };
      });
      setStocks(stocksMap);
    };

    socket.on('balance-update', handleBalanceUpdate);
    socket.on('position-update', handlePositionUpdate);
    socket.on('market-status', handleMarketStatus);
    socket.on('market-bell', handleMarketBell);
    socket.on('stock-updates', handleStockUpdates);
    socket.on('market-data', handleMarketData);

    // Request initial market status via socket
    socket.emit('get-market-status');

    // Also fetch via HTTP as fallback (socket may not respond immediately)
    const fetchMarketStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/market/status`);
        if (response.ok) {
          const data = await response.json();
          const ms = data.marketState || data;
          setMarketState(prev => ({
            ...prev,
            isOpen: ms.isOpen ?? prev.isOpen,
            openTime: ms.openTime ?? prev.openTime,
            closeTime: ms.closeTime ?? prev.closeTime
          }));
        }
      } catch (e) {
        console.error('Failed to fetch market status:', e);
      }
    };
    fetchMarketStatus();

    return () => {
      socket.off('balance-update', handleBalanceUpdate);
      socket.off('position-update', handlePositionUpdate);
      socket.off('market-status', handleMarketStatus);
      socket.off('market-bell', handleMarketBell);
      socket.off('stock-updates', handleStockUpdates);
      socket.off('market-data', handleMarketData);
    };
  }, [userId, isLoggedIn]);

  // Recalculate portfolio values when stocks change
  useEffect(() => {
    if (Object.keys(stocks).length === 0 || accountData.positions.length === 0) return;

    const updatedPositions = accountData.positions.map(pos => {
      const stock = stocks[pos.stockId];
      if (!stock) return pos;

      const currentPrice = stock.currentPrice;
      const currentValue = pos.type === 'LONG'
        ? currentPrice * pos.shares
        : (2 * pos.entryPrice - currentPrice) * pos.shares;
      const profit = pos.type === 'LONG'
        ? (currentPrice - pos.entryPrice) * pos.shares
        : (pos.entryPrice - currentPrice) * pos.shares;

      return {
        ...pos,
        currentPrice,
        currentValue: parseFloat(currentValue.toFixed(2)),
        profit: parseFloat(profit.toFixed(2))
      };
    });

    const positionsValue = updatedPositions.reduce((sum, pos) => sum + (pos.currentValue || 0), 0);
    const totalValue = parseFloat((accountData.balance + positionsValue).toFixed(2));
    const performancePercent = accountData.initialBalance > 0
      ? parseFloat(((totalValue - accountData.initialBalance) / accountData.initialBalance * 100).toFixed(2))
      : 0;

    setAccountData(prev => ({
      ...prev,
      positions: updatedPositions,
      totalValue,
      performancePercent
    }));
  }, [stocks]);

  const positionCount = accountData.positions.length;
  const longPositions = accountData.positions.filter(p => p.type === 'LONG').length;
  const shortPositions = accountData.positions.filter(p => p.type === 'SHORT').length;

  // Collapsed view - small floating button at bottom right
  if (!isExpanded) {
    return (
      <button
        className="compact-header-toggle"
        onClick={() => setIsExpanded(true)}
        aria-label="Show account info"
      >
        <span className="toggle-icon">
          {marketState.isOpen ? '🟢' : '🔴'}
        </span>
        {isLoggedIn && (
          <span className={`toggle-balance ${accountData.performancePercent >= 0 ? 'positive' : 'negative'}`}>
            <FlickerText value={formatCurrency(accountData.totalValue)} />
          </span>
        )}
        <span className="toggle-arrow">▲</span>
      </button>
    );
  }

  // Expanded view - full info panel above ticker
  return (
    <div className="compact-header expanded">
      <button
        className="header-collapse-btn"
        onClick={() => setIsExpanded(false)}
        aria-label="Hide account info"
      >
        ▼
      </button>

      <div className="header-section market-status">
        <span className="header-label">MARKET</span>
        <span className={`header-value ${marketState.isOpen ? 'positive' : 'negative'}`}>
          {marketState.isOpen ? '🟢 OPEN' : '🔴 CLOSED'}
        </span>
      </div>

      {isLoggedIn && userId ? (
        <>
          <div className="header-section balance">
            <span className="header-label">TOTAL</span>
            <span className="header-value">
              <FlickerText value={formatCurrency(accountData.totalValue)} />
            </span>
          </div>

          <div className="header-section performance">
            <span className="header-label">P/L</span>
            <span className={`header-value ${accountData.performancePercent >= 0 ? 'positive' : 'negative'}`}>
              <FlickerText value={`${accountData.performancePercent >= 0 ? '+' : ''}${accountData.performancePercent.toFixed(2)}%`} />
            </span>
          </div>

          <div className="header-section positions">
            <span className="header-label">POS</span>
            <span className="header-value">
              {positionCount > 0 ? (
                <>
                  <span className="position-count">{positionCount}</span>
                  {longPositions > 0 && <span className="long-count">L:{longPositions}</span>}
                  {shortPositions > 0 && <span className="short-count">S:{shortPositions}</span>}
                </>
              ) : (
                '0'
              )}
            </span>
          </div>

          <div className="header-section cash">
            <span className="header-label">CASH</span>
            <span className="header-value">
              <FlickerText value={formatCurrency(accountData.balance)} />
            </span>
          </div>
        </>
      ) : (
        <div className="header-section">
          <span className="header-label">STATUS</span>
          <span className="header-value">NOT LOGGED IN</span>
        </div>
      )}
    </div>
  );
};

export default CompactHeader;
